/**
 * Dashboard Service
 * Handles dashboard layout and widget data API calls
 */

import { apiClient, ApiResponse } from './api.client';
import { DashboardLayoutState, DashboardWidgetsData, WidgetDateRange } from '@creatoros/shared';

/**
 * Dashboard Service Class
 */
class DashboardService {
  private readonly layoutPath = '/api/users/me/dashboard-layout';
  private readonly widgetsPath = '/api/dashboard/widgets';

  /**
   * Get the user's saved dashboard layout
   * @returns Dashboard layout state or null if not found
   */
  async getLayout(): Promise<DashboardLayoutState | null> {
    try {
      const response = await apiClient.get<ApiResponse<DashboardLayoutState>>(this.layoutPath);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      return null;
    } catch (error: any) {
      // Return null if no layout found (404), throw for other errors
      if (error.response?.status === 404) {
        return null;
      }

      throw new Error('Failed to fetch dashboard layout');
    }
  }

  /**
   * Save the user's dashboard layout
   * @param layout - Dashboard layout state to save
   * @returns Saved dashboard layout with server-side timestamp
   */
  async saveLayout(layout: DashboardLayoutState): Promise<DashboardLayoutState> {
    // Send layout without updatedAt - backend will set it
    const { updatedAt: _updatedAt, ...layoutToSave } = layout;

    const response = await apiClient.put<ApiResponse<DashboardLayoutState>>(
      this.layoutPath,
      layoutToSave
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to save dashboard layout');
  }

  /**
   * Get all widget data for the dashboard
   * @param dateRange - Optional date range filter
   * @param widgets - Optional list of widget IDs to fetch data for
   * @returns Dashboard widgets data
   */
  async getWidgets(dateRange?: WidgetDateRange, widgets?: string[]): Promise<DashboardWidgetsData> {
    const params: Record<string, string> = {};
    if (dateRange) params.dateRange = dateRange;
    if (widgets && widgets.length > 0) params.widgets = widgets.join(',');

    const response = await apiClient.get<ApiResponse<DashboardWidgetsData>>(this.widgetsPath, {
      params,
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch dashboard widgets');
  }
}

/**
 * Export singleton instance
 */
export const dashboardService = new DashboardService();

/**
 * Export default
 */
export default dashboardService;
